import { useForm } from "@/src/provider/Form/FormProvider";
import { useState } from "react";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Button, InputAdornment, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Group from "@/src/components/Wizard/Group";
import wizardStyle from "@/src/components/Form/wizardStyle";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { translate } from "@/src/services/translations/translations";
import { minStringLength, requiredValidator } from "@/src/services/validationRules";
import YearFieldConfigured from "@/src/components/Form/ConfiguredFormElements/YearFieldConfigured/YearFieldConfigured";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import DateFieldConfigured from "@/src/components/Form/ConfiguredFormElements/DateFieldConfigured/DateFieldConfigured";
import ValidatorMultiSelectFieldWithCheckboxes from "@/src/components/FormFields/ValidatorMultiSelectFieldWithCheckboxes";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import WitnessData from "@/src/components/Form/AbstractLitigationCase/WitnessData";
import IconVacation from "@/src/assets/icons/employment/toggleable/IconVacation";
import IconOvertime from "@/src/assets/icons/employment/toggleable/IconOvertime";
import IconWorkCertificate from "@/src/assets/icons/employment/toggleable/IconWorkCertificate";
import IconParentalLeave from "@/src/assets/icons/employment/toggleable/IconParentalLeave";
import IconPartTime from "@/src/assets/icons/employment/toggleable/IconPartTime";
import IconBullying from "@/src/assets/icons/employment/toggleable/IconBullying";
import IconQuestionmark from "@/src/assets/icons/common/toggleable/IconQuestionmark";
import IconImportant from "@/src/assets/icons/common/toggleable/IconImportant";
import IconNoVacation from "@/src/assets/icons/employment/toggleable/IconNoVacation";
import IconEditWorkCertificate from "@/src/assets/icons/employment/toggleable/IconEditWorkCertificate";
import IconOpenDispute from "@/src/assets/icons/employment/toggleable/IconOpenDispute";
import IconNoOvertime from "@/src/assets/icons/employment/toggleable/IconNoOvertime";
import IconNoReasonsGiven from "@/src/assets/icons/employment/toggleable/IconNoReasonsGiven";
import IconErrorInContract from "@/src/assets/icons/employment/toggleable/IconErrorInContract";
import IconDefend from "@/src/assets/icons/contract/toggleable/IconDefend";
import TextFieldConfigured from "@/src/components/Form/ConfiguredFormElements/TextFieldConfigured/TextFieldConfigured";
import IconPriceIncrease from "@/src/assets/icons/provider/toggleable/IconPriceIncrease";
import IconRequirementNotMet from "@/src/assets/icons/employment/toggleable/IconRequirementNotMet";
import IconDiscrimination from "@/src/assets/icons/employment/toggleable/IconDiscrimination";
import IconEnforce from "@/src/assets/icons/contract/toggleable/IconEnforce";
import IconTime from "@/src/assets/icons/contract/toggleable/IconTime";
import IconClose from "@/src/assets/icons/common/toggleable/IconClose";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import MoreInfoIcon from "@/src/components/SvgIcon/MoreInfoIcon";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import MuiListNativeStyled from "@/src/components/List/MuiListNativeStyled";
import IconMoneyAndWork from "@/src/assets/icons/employment/toggleable/IconMoneyAndWork";
import IconMoneyAndOvertime from "@/src/assets/icons/employment/toggleable/IconMoneyAndOvertime";
import IconMoneyAndVacation from "@/src/assets/icons/employment/toggleable/IconMoneyAndVacation";
import InfoIconButton from "@/src/components/Button/InfoIconButton";
import { arrayUtils } from "@/src/services/utils";

type EmploymentContractDetailsEnforceClaimProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function EmploymentContractDetailsEnforceClaim({ product, submitProps }: EmploymentContractDetailsEnforceClaimProps) {
  const { values } = useForm();

  const [bullyingModalOpen, setBullyingModalOpen] = useState(false);
  const [grossSalarySumLastThreeMonthsModalOpen, setGrossSalarySumLastThreeMonthsModalOpen] = useState(false);

  const getReasonsForDisputeFilteredOptions = (subjectOfDispute: string) => {
    const options = [];

    const sharedOptions = [
      {
        labelText: translate("employmentContract.reasonsForDispute.options.workCertificate.label", values),
        labelIcon: <IconWorkCertificate />,
        labelIconActive: <IconWorkCertificate active />,
        optionHintText: translate("employmentContract.reasonsForDispute.options.workCertificate.optionHintText", values),
        value: "workCertificate",
      },
      {
        labelText: translate("employmentContract.reasonsForDispute.options.parentalLeave.label", values),
        labelIcon: <IconParentalLeave />,
        labelIconActive: <IconParentalLeave active />,
        optionHintText: translate("employmentContract.reasonsForDispute.options.parentalLeave.optionHintText", values),
        value: "parentalLeave",
      },
      {
        labelText: translate("employmentContract.reasonsForDispute.options.partTime.label", values),
        labelIcon: <IconPartTime />,
        labelIconActive: <IconPartTime active />,
        optionHintText: translate("employmentContract.reasonsForDispute.options.partTime.optionHintText", values),
        value: "partTime",
      },
      {
        labelText: translate("employmentContract.reasonsForDispute.options.bullying.label", values),
        labelIcon: <IconBullying />,
        labelIconActive: <IconBullying active />,
        optionHintText: translate("employmentContract.reasonsForDispute.options.bullying.optionHintText", values),
        value: "bullying",
      },
      {
        labelText: translate("employmentContract.reasonsForDispute.options.other.label", values),
        labelIcon: <IconQuestionmark />,
        labelIconActive: <IconQuestionmark active />,
        optionHintText: translate("employmentContract.reasonsForDispute.options.other.optionHintText", values),
        value: "other",
      },
    ];

    if (subjectOfDispute === "moneyAndGoods") {
      options.push(...sharedOptions);
    }
    if (subjectOfDispute === "goods") {
      options.push(
        {
          labelText: translate("employmentContract.reasonsForDispute.options.vacation.label", values),
          labelIcon: <IconVacation />,
          labelIconActive: <IconVacation active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.vacation.optionHintText", values),
          value: "vacation",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.overtime.label", values),
          labelIcon: <IconOvertime />,
          labelIconActive: <IconOvertime active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.overtime.optionHintText", values),
          value: "overtime",
        },
        ...sharedOptions,
      );
    }
    if (subjectOfDispute === "money") {
      options.push(
        {
          labelText: translate("employmentContract.reasonsForDispute.options.vacation.label", values),
          labelIcon: <IconMoneyAndVacation />,
          labelIconActive: <IconMoneyAndVacation active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.vacation.optionHintText", values),
          value: "vacation",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.overtime.label", values),
          labelIcon: <IconMoneyAndOvertime />,
          labelIconActive: <IconMoneyAndOvertime active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.overtime.optionHintText", values),
          value: "overtime",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.salary.label", values),
          labelIcon: <IconMoneyAndWork />,
          labelIconActive: <IconMoneyAndWork active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.salary.optionHintText", values),
          value: "salary",
        },
      );
    }

    return options;
  };

  return (
    <>
      <Grid size={12}>
        <Group
          type={"checkbox"}
          name={"reasonsForDispute"}
          question={translate("employmentContract.reasonsForDispute.question", values)}
          options={getReasonsForDisputeFilteredOptions(values.subjectOfDispute)}
        />
      </Grid>
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["vacation"]) && values.subjectOfDispute !== "money" && (
        <>
          <Grid size={12}>
            <FormLegend>Um Ihren Anspruch auf die Gewährung von Urlaubstagen genau zu erfassen, benötigen wir die folgenden Informationen.</FormLegend>
          </Grid>

          <YearFieldConfigured caseData={values} fieldPath={"yearOfVacationClaim"} required />

          <NumberFieldConfigured caseData={values} fieldPath={"vacationDaysLeft"} numberType={"float"} required />

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"vacationDenialReason"}
              question={translate("employmentContract.vacationDenialReason.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.vacationDenialReason.options.operationalNecessity.label", values),
                  labelIcon: <IconImportant />,
                  labelIconActive: <IconImportant active />,
                  optionHintText: translate("employmentContract.vacationDenialReason.options.operationalNecessity.optionHintText", values),
                  value: "operationalNecessity",
                },
                {
                  labelText: translate("employmentContract.vacationDenialReason.options.noVacationDays.label", values),
                  labelIcon: <IconNoVacation />,
                  labelIconActive: <IconNoVacation active />,
                  optionHintText: translate("employmentContract.vacationDenialReason.options.noVacationDays.optionHintText", values),
                  value: "noVacationDays",
                },
                {
                  labelText: translate("employmentContract.vacationDenialReason.options.requestNotOnTime.label", values),
                  labelIcon: <IconTime />,
                  labelIconActive: <IconTime active />,
                  value: "requestNotOnTime",
                },
                {
                  labelText: translate("employmentContract.vacationDenialReason.options.noReasonProvided.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "noReasonProvided",
                },
                {
                  labelText: translate("employmentContract.vacationDenialReason.options.otherReason.label", values),
                  labelIcon: <IconQuestionmark />,
                  labelIconActive: <IconQuestionmark active />,
                  optionHintText: translate("employmentContract.vacationDenialReason.options.otherReason.optionHintText", values),
                  value: "otherReason",
                },
              ]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["overtime"]) && values.subjectOfDispute !== "money" && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>
              Um Ihren Anspruch auf Freizeitausgleich für Überstunden genau zu erfassen, benötigen wir den Zeitraum, in dem die Überstunden angefallen sind, und
              die ungefähre Anzahl der Stunden.
            </FormLegend>
          </Grid>

          <DateFieldConfigured caseData={values} fieldPath={"overtimeAccumulatedStartDate"} dateValidation={"past"} required />
          <DateFieldConfigured caseData={values} fieldPath={"overtimeAccumulatedEndDate"} dateValidation={"pastOrToday"} required />
          <NumberFieldConfigured caseData={values} fieldPath={"hoursLeisureCompensation"} numberType={"float"} required />

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <Group
              type={"radio"}
              name={"hasOvertimeRecord"}
              question={translate("employmentContract.hasOvertimeRecord.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.hasOvertimeRecord.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.hasOvertimeRecord.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["workCertificate"]) && (
        <>
          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <Group
              type={"radio"}
              name={"workCertificateRequest"}
              question={translate("employmentContract.workCertificateRequest.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.workCertificateRequest.options.requestIssuance.label", values),
                  labelIcon: <IconWorkCertificate />,
                  labelIconActive: <IconWorkCertificate active />,
                  value: "requestIssuance",
                },
                {
                  labelText: translate("employmentContract.workCertificateRequest.options.requestCorrection.label", values),
                  labelIcon: <IconEditWorkCertificate />,
                  labelIconActive: <IconEditWorkCertificate active />,
                  value: "requestCorrection",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          {values.workCertificateRequest === "requestIssuance" && (
            <>
              <Grid size={12}>
                <Group
                  type={"radio"}
                  name={"workCertificateIssuanceDenied"}
                  question={translate("employmentContract.workCertificateIssuanceDenied.question", values)}
                  options={[
                    {
                      labelText: translate("employmentContract.workCertificateIssuanceDenied.options.yes.label", values),
                      labelIcon: <CheckIcon />,
                      labelIconActive: <CheckIcon active />,
                      value: "yes",
                    },
                    {
                      labelText: translate("employmentContract.workCertificateIssuanceDenied.options.no.label", values),
                      labelIcon: <CrossIcon />,
                      labelIconActive: <CrossIcon active />,
                      value: "no",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>

              {values.workCertificateIssuanceDenied === "yes" && (
                <>
                  <Grid size={12}>
                    <Group
                      type={"radio"}
                      name={"workCertificateIssuanceDenialReason"}
                      question={translate("employmentContract.workCertificateIssuanceDenialReason.question", values)}
                      options={[
                        {
                          labelText: translate("employmentContract.workCertificateIssuanceDenialReason.options.noEntitlement.label", values),
                          labelIcon: <IconClose />,
                          labelIconActive: <IconClose active />,
                          optionHintText: translate("employmentContract.workCertificateIssuanceDenialReason.options.noEntitlement.optionHintText", values),
                          value: "noEntitlement",
                        },
                        {
                          labelText: translate("employmentContract.workCertificateIssuanceDenialReason.options.unresolvedDispute.label", values),
                          labelIcon: <IconOpenDispute />,
                          labelIconActive: <IconOpenDispute active />,
                          optionHintText: translate("employmentContract.workCertificateIssuanceDenialReason.options.unresolvedDispute.optionHintText", values),
                          value: "unresolvedDispute",
                        },
                        {
                          labelText: translate("employmentContract.workCertificateIssuanceDenialReason.options.insufficientCapacity.label", values),
                          labelIcon: <IconNoOvertime />,
                          labelIconActive: <IconNoOvertime active />,
                          value: "insufficientCapacity",
                        },
                        {
                          labelText: translate("employmentContract.workCertificateIssuanceDenialReason.options.noReasonProvided.label", values),
                          labelIcon: <IconNoReasonsGiven />,
                          labelIconActive: <IconNoReasonsGiven active />,
                          value: "noReasonProvided",
                        },
                        {
                          labelText: translate("employmentContract.workCertificateIssuanceDenialReason.options.other.label", values),
                          labelIcon: <IconQuestionmark />,
                          labelIconActive: <IconQuestionmark active />,
                          optionHintText: translate("employmentContract.workCertificateIssuanceDenialReason.options.other.optionHintText", values),
                          value: "other",
                        },
                      ]}
                      validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          {values.workCertificateRequest === "requestCorrection" && (
            <>
              <DateFieldConfigured caseData={values} fieldPath={"workCertificateReceivedAtDate"} required />

              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <FormLegend>{translate("employmentContract.workCertificateCorrectionRequest.question", values)}</FormLegend>
              </Grid>
              <Grid size={12}>
                <ValidatorMultiSelectFieldWithCheckboxes
                  label={translate("employmentContract.workCertificateCorrectionRequest.label", values)}
                  name={"workCertificateCorrectionRequest"}
                  options={[
                    {
                      value: "performanceEvaluation",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.performanceEvaluation.label", values),
                    },
                    {
                      value: "conductEvaluation",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.conductEvaluation.label", values),
                    },
                    {
                      value: "incompleteJobDescription",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.incompleteJobDescription.label", values),
                    },
                    {
                      value: "missingKeyAchievements",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.missingKeyAchievements.label", values),
                    },
                    {
                      value: "factualErrors",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.factualErrors.label", values),
                    },
                    {
                      value: "formalErrors",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.formalErrors.label", values),
                    },
                    {
                      value: "negativePhrasingOrCodes",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.negativePhrasingOrCodes.label", values),
                    },
                    {
                      value: "inadequateClosingStatement",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.inadequateClosingStatement.label", values),
                    },
                    {
                      value: "other",
                      label: translate("employmentContract.workCertificateCorrectionRequest.options.other.label", values),
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            </>
          )}
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["parentalLeave"]) && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>
              Um Sie bei der Durchsetzung Ihrer Rechte bezüglich der Elternzeit unterstützen zu können, bitten wir Sie um die folgenden Angaben.
            </FormLegend>
          </Grid>

          <DateFieldConfigured caseData={values} fieldPath={"parentalLeaveStartDate"} required />
          <DateFieldConfigured caseData={values} fieldPath={"parentalLeaveEndDate"} required />

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"hasTakenParentalLeaveBefore"}
              question={translate("employmentContract.hasTakenParentalLeaveBefore.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.hasTakenParentalLeaveBefore.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.hasTakenParentalLeaveBefore.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"parentalLeaveSplit"}
              question={translate("employmentContract.parentalLeaveSplit.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.parentalLeaveSplit.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.parentalLeaveSplit.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"parentalLeaveRequestOnTime"}
              question={translate("employmentContract.parentalLeaveRequestOnTime.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.parentalLeaveRequestOnTime.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.parentalLeaveRequestOnTime.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"parentalLeaveRequestDenied"}
              question={translate("employmentContract.parentalLeaveRequestDenied.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.parentalLeaveRequestDenied.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.parentalLeaveRequestDenied.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          {values.parentalLeaveRequestDenied === "yes" && (
            <>
              <Grid size={12}>
                <Group
                  type={"radio"}
                  name={"parentalLeaveRequestDeniedReason"}
                  question={translate("employmentContract.parentalLeaveRequestDeniedReason.question", values)}
                  options={[
                    {
                      labelText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.requestNotOnTime.label", values),
                      labelIcon: <IconNoOvertime />,
                      labelIconActive: <IconNoOvertime active />,
                      optionHintText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.requestNotOnTime.optionHintText", values),
                      value: "requestNotOnTime",
                    },
                    {
                      labelText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.operationalNecessity.label", values),
                      labelIcon: <IconImportant />,
                      labelIconActive: <IconImportant active />,
                      optionHintText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.operationalNecessity.optionHintText", values),
                      value: "operationalNecessity",
                    },
                    {
                      labelText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.formalErrors.label", values),
                      labelIcon: <IconErrorInContract />,
                      labelIconActive: <IconErrorInContract active />,
                      optionHintText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.formalErrors.optionHintText", values),
                      value: "formalErrors",
                    },
                    {
                      labelText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.entitlementDisputedByEmployer.label", values),
                      labelIcon: <IconDefend />,
                      labelIconActive: <IconDefend active />,
                      value: "entitlementDisputedByEmployer",
                    },
                    {
                      labelText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.noReasonProvided.label", values),
                      labelIcon: <IconNoReasonsGiven />,
                      labelIconActive: <IconNoReasonsGiven active />,
                      value: "noReasonProvided",
                    },
                    {
                      labelText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.other.label", values),
                      labelIcon: <IconQuestionmark />,
                      labelIconActive: <IconQuestionmark active />,
                      optionHintText: translate("employmentContract.parentalLeaveRequestDeniedReason.options.other.optionHintText", values),
                      value: "other",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            </>
          )}
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["partTime"]) && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>
              Um Sie bei der Durchsetzung Ihrer Rechte bezüglich der Teilzeit unterstützen zu können, bitten wir Sie um die folgenden Angaben.
            </FormLegend>
          </Grid>

          <DateFieldConfigured caseData={values} fieldPath={"partTimeStartDate"} required />
          <NumberFieldConfigured caseData={values} fieldPath={"partTimeDesiredWeeklyWorkingHours"} numberType={"float"} required />

          <TextFieldConfigured caseData={values} fieldPath={"numberOfEmployees"} required />

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"partTimeDenialReason"}
              question={translate("employmentContract.partTimeDenialReason.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.partTimeDenialReason.options.operationalReasons.label", values),
                  labelIcon: <IconImportant />,
                  labelIconActive: <IconImportant active />,
                  optionHintText: translate("employmentContract.partTimeDenialReason.options.operationalReasons.optionHintText", values),
                  value: "operationalReasons",
                },
                {
                  labelText: translate("employmentContract.partTimeDenialReason.options.disproportionateCosts.label", values),
                  labelIcon: <IconPriceIncrease />,
                  labelIconActive: <IconPriceIncrease active />,
                  value: "disproportionateCosts",
                },
                {
                  labelText: translate("employmentContract.partTimeDenialReason.options.requirementsNotMet.label", values),
                  labelIcon: <IconRequirementNotMet />,
                  labelIconActive: <IconRequirementNotMet active />,
                  optionHintText: translate("employmentContract.partTimeDenialReason.options.requirementsNotMet.optionHintText", values),
                  value: "requirementsNotMet",
                },
                {
                  labelText: translate("employmentContract.partTimeDenialReason.options.formalErrors.label", values),
                  labelIcon: <IconErrorInContract />,
                  labelIconActive: <IconErrorInContract active />,
                  optionHintText: translate("employmentContract.partTimeDenialReason.options.formalErrors.optionHintText", values),
                  value: "formalErrors",
                },
                {
                  labelText: translate("employmentContract.partTimeDenialReason.options.noReasonProvided.label", values),
                  labelIcon: <IconNoReasonsGiven />,
                  labelIconActive: <IconNoReasonsGiven active />,
                  value: "noReasonProvided",
                },
                {
                  labelText: translate("employmentContract.partTimeDenialReason.options.other.label", values),
                  labelIcon: <IconQuestionmark />,
                  labelIconActive: <IconQuestionmark active />,
                  optionHintText: translate("employmentContract.partTimeDenialReason.options.other.optionHintText", values),
                  value: "other",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["bullying"]) && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>
              Um Sie bei Mobbing oder Diskriminierung unterstützen zu können, benötigen wir eine detaillierte Beschreibung der Vorfälle und Ihrer Ziele.
              <Button fullWidth={true} variant={"text"} color={"primary"} onClick={() => setBullyingModalOpen(true)}>
                <MoreInfoIcon sx={wizardStyle.leftIcon} /> Weitere Informationen
              </Button>
            </FormLegend>
          </Grid>

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"incidentType"}
              question={translate("employmentContract.incidentType.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.incidentType.options.bullying.label", values),
                  labelIcon: <IconBullying />,
                  labelIconActive: <IconBullying active />,
                  optionHintText: translate("employmentContract.incidentType.options.bullying.optionHintText", values),
                  value: "bullying",
                },
                {
                  labelText: translate("employmentContract.incidentType.options.discrimination.label", values),
                  labelIcon: <IconDiscrimination />,
                  labelIconActive: <IconDiscrimination active />,
                  optionHintText: translate("employmentContract.incidentType.options.discrimination.optionHintText", values),
                  value: "discrimination",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          <WitnessData product={product} />

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <DateFieldConfigured caseData={values} fieldPath={"incidentStartDate"} />
          </Grid>

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"incidentObjectiveClient"}
              question={translate("employmentContract.incidentObjectiveClient.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.incidentObjectiveClient.options.injunction.label", values),
                  labelIcon: <IconDefend />,
                  labelIconActive: <IconDefend active />,
                  value: "injunction",
                },
                {
                  labelText: translate("employmentContract.incidentObjectiveClient.options.compensation.label", values),
                  labelIcon: <IconEnforce />,
                  labelIconActive: <IconEnforce active />,
                  value: "compensation",
                },
                {
                  labelText: translate("employmentContract.incidentObjectiveClient.options.other.label", values),
                  labelIcon: <IconQuestionmark />,
                  labelIconActive: <IconQuestionmark active />,
                  optionHintText: translate("employmentContract.incidentObjectiveClient.options.other.optionHintText", values),
                  value: "other",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["salary"]) && (
        <>
          <DateFieldConfigured caseData={values} fieldPath={"salaryOutstandingStartDate"} dateValidation={"past"} required />
          <DateFieldConfigured caseData={values} fieldPath={"salaryOutstandingEndDate"} dateValidation={"pastOrToday"} required />
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["overtime"]) && values.subjectOfDispute === "money" && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>
              Um Ihren Anspruch auf Überstundenvergütung genau zu erfassen, benötigen wir den Zeitraum, in dem die Überstunden angefallen sind, und die
              ungefähre Anzahl der Stunden.
            </FormLegend>
          </Grid>

          <DateFieldConfigured caseData={values} fieldPath={"overtimeAccumulatedStartDate"} dateValidation={"past"} required />
          <DateFieldConfigured caseData={values} fieldPath={"overtimeAccumulatedEndDate"} dateValidation={"pastOrToday"} required />
          <NumberFieldConfigured caseData={values} fieldPath={"overtimeAccumulatedTotal"} numberType={"float"} required />
          <NumberFieldConfigured caseData={values} fieldPath={"weeklyWorkingHours"} numberType={"float"} />

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <Group
              type={"radio"}
              name={"hasOvertimeSurcharge"}
              question={translate("employmentContract.hasOvertimeSurcharge.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.hasOvertimeSurcharge.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.hasOvertimeSurcharge.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          {values.hasOvertimeSurcharge === "yes" && (
            <>
              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <FormLegend>Bitte geben Sie an, ob und welche Regelungen zu Überstundenzuschlägen für Sie gelten.</FormLegend>
              </Grid>
              <Grid size={12}>
                <ValidatorTextField
                  name={"overtimeSurchargeDetails"}
                  label={translate("employmentContract.overtimeSurchargeDetails.label", values)}
                  validators={[
                    requiredValidator(
                      "Bitte schildern Sie detailliert, welche Bewertungen oder Inhalte Sie für fehlerhaft oder sind oder korrigiert werden müssen",
                    ),
                    { validator: minStringLength, params: [50], message: "Bitte geben Sie mindestens 50 Zeichen ein" },
                  ]}
                  helperText={translate("employmentContract.overtimeSurchargeDetails.helperText", values)}
                  multiline
                />
              </Grid>
            </>
          )}

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <Group
              type={"radio"}
              name={"hasOvertimeRecord"}
              question={translate("employmentContract.hasOvertimeRecord.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.hasOvertimeRecord.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.hasOvertimeRecord.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["vacation"]) && values.subjectOfDispute === "money" && (
        <>
          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>
              Um Ihren Anspruch auf Urlaubsabgeltung genau zu erfassen, benötigen wir Informationen zu den offenen Urlaubstagen bei Beendigung Ihres
              Arbeitsverhältnisses.
            </FormLegend>
          </Grid>

          <YearFieldConfigured caseData={values} fieldPath={"yearOfVacationClaim"} required />

          <NumberFieldConfigured caseData={values} fieldPath={"vacationDaysLeft"} numberType={"float"} required />

          <NumberFieldConfigured
            caseData={values}
            fieldPath={"grossSalarySumLastThreeMonths"}
            required
            fieldType={"money"}
            numberType={"float"}
            inputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InfoIconButton onClick={() => setGrossSalarySumLastThreeMonthsModalOpen(true)} />
                </InputAdornment>
              ),
            }}
          />

          <NumberFieldConfigured caseData={values} fieldPath={"weeklyWorkingHours"} numberType={"float"} />
        </>
      )}
      <DialogModal open={bullyingModalOpen} onClose={() => setBullyingModalOpen(false)} title={"Unterscheidung zwischen Mobbing und Diskriminierung"}>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Mobbing beschreibt systematische, wiederholte und über einen längeren Zeitraum andauernde Schikanen oder feindseliges Verhalten am Arbeitsplatz. Es
          zielt oft darauf ab, eine Person zu isolieren, zu demotivieren oder sogar aus dem Arbeitsverhältnis zu drängen.
        </Typography>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Diskriminierung hingegen ist eine Ungleichbehandlung, die aufgrund eines oder mehrerer gesetzlich geschützter Merkmale erfolgt. Das Allgemeine
          Gleichbehandlungsgesetz (AGG) verbietet solche Benachteiligungen beispielsweise wegen des Geschlechts, Alters, der Herkunft, Religion oder einer
          Behinderung. Hier kann bereits ein einmaliger Vorfall relevant sein.
        </Typography>
      </DialogModal>
      <DialogModal
        open={grossSalarySumLastThreeMonthsModalOpen}
        onClose={() => setGrossSalarySumLastThreeMonthsModalOpen(false)}
        title={"Info zum Bruttoverdienst der letzten 3 Monate"}
      >
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Bitte tragen Sie hier die <strong>Gesamtsumme Ihres Bruttoverdienstes der letzten drei Monate</strong> vor dem relevanten Ereignis (z.B. Vertragsende)
          ein. <br />
          <br />
          <strong>Wichtig:</strong> Berücksichtigt werden dabei nur Ihr <strong>Grundgehalt</strong> und <strong>laufende, regelmäßige Zuschläge,</strong> die
          Sie für Ihre normale Arbeitsleistung erhalten haben (z.B. für Nacht- oder Feiertagsarbeit).
          <br />
          <br />
          <strong>Nicht mit eingerechnet werden sollten:</strong>
        </Typography>
        <MuiListNativeStyled
          listItems={[
            "Einmalzahlungen (z.B. Weihnachtsgeld, Boni, Urlaubsgeld)",
            "Sachbezüge (z.B. Privatnutzung eines Firmenwagens)",
            "Vergütung für Überstunden",
          ]}
        />
        <br />
        <Typography>
          Geben Sie hier die Anzahl der Personen an, die außer Ihnen noch in dem Wohnraum wohnen. Wenn Sie alleine wohnen, geben Sie hier “0” an.
        </Typography>
      </DialogModal>
    </>
  );
}
